import React, { PureComponent } from 'react';
import {
  arrayOf,
  bool,
  func,
  instanceOf,
  number,
  object,
  shape,
  string
} from 'prop-types';
import { creditType, instanceType } from 'types';

import * as yup from 'yup';
import { get } from 'lodash';
import { Field } from 'formik';
import { components } from 'react-select';
import moment from 'moment';

import Button, { GhostButton } from 'ui/Button';
import DatePicker from 'ui/DatePicker';
import Form, { FormBody, FormFooter, FormSection } from 'ui/Form';
import InputWrapper from 'components/InputWrapper';
import LoadingIndicator from 'ui/LoadingIndicator';
import Message from 'components/Message';
import RadioLabel from 'ui/RadioLabel';
import Select from 'ui/Select';

import {
  DAY_SECONDS,
  FUTURE,
  NOW,
  SIMPLE_DATE_FORMAT,
  SIMPLE_TIME_FORMAT
} from 'utils/constants';
import { getFormattedDateFromTimezone } from 'utils/dates';

import styled from 'styled-components/macro';
import * as styles from './styles';
import Input from '../../ui/Input';

const GroupInput = styled(Input)`
  &:disabled {
    background-color: #eeeeed;
  }
`;

export default class InnerForm extends PureComponent {
  // TODO: implement it here until discussion on best practices
  getSelectedInstance = () =>
    this.props.instances.find(({ id }) => id === this.props.values.instanceId);

  // TODO: implement it here until discussion on best practices
  getSelectedCredit = () =>
    this.props.credits.find(({ id }) => id === this.props.values.creditId);

  getElementError = (element) =>
    this.props.touched[element] && this.props.errors[element];

  InstanceOption = (props) => (
    <components.Option {...props}>
      {this.renderInstanceOption(props.data)}
    </components.Option>
  );

  CreditTypeOption = (props) => (
    <components.Option {...props}>
      {this.renderCreditTypeOption(props.data)}
    </components.Option>
  );

  CreditTypeSelectedValue = (props) => (
    <components.SingleValue {...props}>
      {this.renderCreditTypeOption(props.data)}
    </components.SingleValue>
  );

  // todo refactor to a separate component
  // eslint-disable-next-line class-methods-use-this
  renderInstanceOption = (option) => (
    <styles.InstanceOption>
      <span>{option.alias}</span>
      {!option.online && <styles.OptionInfo>(offline)</styles.OptionInfo>}
    </styles.InstanceOption>
  );

  // todo refactor to a separate component
  // eslint-disable-next-line class-methods-use-this
  renderCreditTypeOption = (option) => (
    <styles.InstanceOption>
      <span>{option.credit_type.name}</span>
      <styles.OptionInfo>({option.amount} available)</styles.OptionInfo>
    </styles.InstanceOption>
  );

  renderErrorMessage = () =>
    this.props.errors.description && (
      <Message type={Message.types.ERROR} withMarginBottom>
        {this.props.errors.description}
      </Message>
    );

  renderOfflineMessage = () => {
    const selectedInstance = this.getSelectedInstance();
    const instanceOnline = get(selectedInstance, 'online', true);

    return (
      !instanceOnline && (
        <Message type={Message.types.WARNING} withMarginBottom>
          The selected FlexBox is offline.
        </Message>
      )
    );
  };

  renderCreditGroup = () => (
    <InputWrapper
      label="Group"
      name="credit-group"
      version="v2"
      labelMarginBottom
    >
      <GroupInput
        data-testid="credit-group"
        id="credit-group"
        name="credit-group"
        disabled
        value={this.props.group?.name}
      />
    </InputWrapper>
  );

  renderInstancesDropdown = () => {
    const { errors, instances, setFieldTouched, setFieldValue, touched } =
      this.props;

    const selectedInstance = this.getSelectedInstance();

    const INSTANCE_ID = 'instanceId';
    const instanceIdError = this.getElementError(INSTANCE_ID);
    const instanceIdLabel = 'FlexBox';

    return (
      <FormSection>
        <InputWrapper
          error={instanceIdError}
          fullWidth
          label={instanceIdLabel}
          name={INSTANCE_ID}
        >
          <Select
            config={{
              getOptionLabel: ({ alias }) => alias,
              getOptionValue: ({ id }) => id,
              components: {
                Option: this.InstanceOption
              }
            }}
            haserror={!!errors.instanceId}
            name={INSTANCE_ID}
            onBlur={setFieldTouched}
            onChange={setFieldValue}
            options={instances}
            touched={touched.instanceId}
            value={selectedInstance}
          />
        </InputWrapper>
      </FormSection>
    );
  };

  renderCreditsDropdown = () => {
    const { credits, errors, setFieldTouched, setFieldValue, touched } =
      this.props;

    const selectedCredit = this.getSelectedCredit();

    const CREDIT_ID = 'creditId';
    const creditIdError = this.getElementError(CREDIT_ID);
    const creditIdLabel = 'Credit Type';

    return (
      <FormSection>
        <InputWrapper
          error={creditIdError}
          fullWidth
          label={creditIdLabel}
          name={CREDIT_ID}
        >
          <Select
            config={{
              getOptionLabel: ({ credit_type }) => credit_type.name,
              getOptionValue: ({ id }) => id,
              components: {
                Option: this.CreditTypeOption,
                SingleValue: this.CreditTypeSelectedValue
              }
            }}
            haserror={!!errors.creditId}
            name={CREDIT_ID}
            onBlur={setFieldTouched}
            onChange={setFieldValue}
            options={credits}
            touched={touched.creditId}
            value={selectedCredit}
          />
        </InputWrapper>
      </FormSection>
    );
  };

  renderActivationTime = (activationNow, activationFuture) => {
    const { minDate, setFieldValue, values } = this.props;
    const selectedCredit = this.getSelectedCredit();
    const durationDays = get(
      selectedCredit,
      ['credit_type', 'duration_days'],
      1
    );

    const datePickerOptions = {
      minDate,
      onDayCreate: (dObj, dStr, fp, dayElem) => {
        const { latestSelectedDateObj } = fp;

        if (latestSelectedDateObj) {
          const selectedDate = moment(latestSelectedDateObj);
          const currentDate = moment(dayElem.dateObj);
          const differenceDays = selectedDate.diff(currentDate, 'd');

          // Check if credit active for any part of day
          if (
            differenceDays <= 0 &&
            differenceDays >= (durationDays - 1) * -1
          ) {
            const comparisonTime = moment(currentDate);
            comparisonTime.set({
              hour: selectedDate.get('hour'),
              minute: selectedDate.get('minute')
            });

            const differenceSeconds = comparisonTime.diff(currentDate, 's');
            const selectedPercentage = 100 * (differenceSeconds / DAY_SECONDS);
            dayElem.classList.add('creditActive');

            const selectionSpan = document.createElement('span');
            const textSpan = document.createElement('span');
            const textSpanInner = document.createElement('span');
            textSpan.append(textSpanInner);

            // Credit start date
            // `startOf` modifies the `moment` object, so we have to use `clone` to avoid resetting the time
            if (
              selectedDate
                .clone()
                .startOf('d')
                .isSame(currentDate.clone().startOf('d'))
            ) {
              selectionSpan.setAttribute(
                'style',
                `width: ${100 - selectedPercentage}%;`
              );
              selectionSpan.classList.add('selectedPercentage');

              textSpanInner.textContent = dayElem.textContent;
              textSpan.classList.add('selectedPercentageText');

              dayElem.append(selectionSpan);
              dayElem.append(textSpan);
            }

            // Credit end date
            if (differenceDays === (durationDays - 1) * -1) {
              selectionSpan.setAttribute(
                'style',
                `width: ${
                  selectedPercentage === 0 ? '100' : selectedPercentage
                }%;`
              );
              selectionSpan.classList.add('selectedPercentage');

              textSpanInner.textContent = dayElem.textContent;
              textSpan.classList.add('selectedPercentageText');

              dayElem.classList.add('creditEndDate');
              dayElem.append(selectionSpan);
              dayElem.append(textSpan);
            }
          }
        }
      }
    };

    const ACTIVATION = 'activation';
    const ACTIVATION_NOW = 'activationNow';
    const ACTIVATION_FUTURE = 'activationFuture';

    const SCHEDULE_DATE = 'creditScheduleDate';
    const scheduleDateError = this.getElementError(SCHEDULE_DATE);
    const scheduleDatePlaceholder = activationNow ? '' : 'Start time';
    const scheduleDateValue = activationNow
      ? undefined
      : values.creditScheduleDate;

    return (
      <FormSection>
        <InputWrapper
          error={scheduleDateError}
          fullWidth
          label="Set start time"
          name={ACTIVATION}
        >
          <styles.RadiosWrapper>
            <RadioLabel htmlFor={ACTIVATION_NOW}>
              <Field
                checked={activationNow}
                disabled={!selectedCredit}
                id={ACTIVATION_NOW}
                name={ACTIVATION}
                value={NOW}
                type="radio"
              />
              <span>Activate now</span>
            </RadioLabel>
            <RadioLabel htmlFor={ACTIVATION_FUTURE}>
              <Field
                checked={activationFuture}
                disabled={!selectedCredit}
                id={ACTIVATION_FUTURE}
                name={ACTIVATION}
                value={FUTURE}
                type="radio"
              />
              <span>Schedule for future</span>
              <styles.StyledDatePicker
                as={DatePicker}
                disabled={activationNow || !selectedCredit}
                name={SCHEDULE_DATE}
                options={datePickerOptions}
                onChange={setFieldValue}
                placeholder={scheduleDatePlaceholder}
                value={scheduleDateValue}
              />
            </RadioLabel>
          </styles.RadiosWrapper>
        </InputWrapper>
      </FormSection>
    );
  };

  renderCreditSummary = (activationNow, creditScheduleDate) => {
    const scheduledDate = getFormattedDateFromTimezone(
      creditScheduleDate,
      SIMPLE_DATE_FORMAT
    );
    const scheduledTime = getFormattedDateFromTimezone(
      creditScheduleDate,
      SIMPLE_TIME_FORMAT
    );
    const displayDate = creditScheduleDate
      ? `on ${scheduledDate} at ${scheduledTime}`
      : 'at the time selected';

    const instanceAliasString = get(
      this.getSelectedInstance(),
      'alias',
      'your FlexBox'
    );
    const creditName = get(this.getSelectedCredit(), ['credit_type', 'name']);
    const creditNameString = creditName ? `A ${creditName}` : 'The selected';
    const activationNowMsg = (
      <p>
        {creditNameString} credit will be activated immediately on{' '}
        {instanceAliasString}.
        <span>
          <strong>This action cannot be undone.</strong>
        </span>
      </p>
    );

    const activationFutureMsg = (
      <p>
        {creditNameString} credit will be activated {displayDate} on{' '}
        {instanceAliasString}.
        <span>Credits can be re-scheduled up until their activation time.</span>
      </p>
    );

    return (
      <styles.CreditSummaryMessage as={Message}>
        {activationNow ? activationNowMsg : activationFutureMsg}
      </styles.CreditSummaryMessage>
    );
  };

  renderButtons = () => (
    <FormSection noMarginBottom>
      <GhostButton
        disabled={this.props.isSubmitting}
        name="cancelBtn"
        onClick={this.props.hideModal}
        type="button"
      >
        Cancel
      </GhostButton>
      <Button disabled={this.props.isSubmitting} name="submitBtn" type="submit">
        Confirm
      </Button>
    </FormSection>
  );

  render() {
    const {
      showInstancesDropdown,
      handleSubmit,
      showCreditsDropdown,
      isSubmitting,
      values,
      group
    } = this.props;

    const activationNow = values.activation === NOW;
    const activationFuture = values.activation === FUTURE;

    return (
      <Form onSubmit={handleSubmit}>
        {isSubmitting && <LoadingIndicator cover />}
        <FormBody>
          {this.renderErrorMessage()}
          {this.renderOfflineMessage()}
          {showInstancesDropdown && group && this.renderCreditGroup()}
          {showInstancesDropdown && this.renderInstancesDropdown()}
          {showCreditsDropdown && this.renderCreditsDropdown()}
          {this.renderActivationTime(activationNow, activationFuture)}
          {this.renderCreditSummary(activationNow, values.creditScheduleDate)}
        </FormBody>
        <FormFooter>{this.renderButtons()}</FormFooter>
      </Form>
    );
  }
}

InnerForm.validationSchema = yup.object({
  activation: yup.string(),
  creditId: yup.number().required('(required)'),
  creditScheduleDate: yup.date().when('activation', {
    is: (val) => val === FUTURE,
    then: yup.date().required('(Please specify the start time)')
  }),
  instanceId: yup.number().required('(required)')
});

InnerForm.propTypes = {
  credits: arrayOf(creditType).isRequired,
  showCreditsDropdown: bool,
  showInstancesDropdown: bool,
  errors: object,
  handleSubmit: func,
  hideModal: func.isRequired,
  instances: arrayOf(instanceType).isRequired,
  isSubmitting: bool,
  minDate: instanceOf(Date).isRequired,
  setFieldTouched: func,
  setFieldValue: func,
  touched: object,
  values: shape({
    activation: string,
    creditScheduleDate: instanceOf(Date),
    creditId: number,
    instanceId: number
  }),
  group: shape({
    id: number,
    name: string
  })
};

InnerForm.defaultProps = {
  errors: {},
  handleSubmit: () => {},
  setFieldTouched: () => {},
  setFieldValue: () => {},
  touched: {},
  values: {}
};
